import { readonly, ref } from 'vue';

export type ThemeMode = 'auto' | 'light' | 'dark';

export const ThemeMode = {
  AUTO: 'auto',
  LIGHT: 'light',
  DARK: 'dark'
} as const;

const themeStorageKey = 'knowledgeBase.themeMode';
const validThemeModes = new Set<string>([ThemeMode.AUTO, ThemeMode.LIGHT, ThemeMode.DARK]);
const themeMode = ref<ThemeMode>(readStoredThemeMode());

export const currentThemeMode = readonly(themeMode);

export function initializeThemeMode(): void {
  applyThemeMode(themeMode.value);
}

export function setThemeMode(theme: ThemeMode): void {
  themeMode.value = theme;
  writeStoredThemeMode(theme);
  applyThemeMode(theme);
}

function readStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return ThemeMode.AUTO;
  }

  try {
    return normalizeThemeMode(window.localStorage.getItem(themeStorageKey));
  } catch {
    return ThemeMode.AUTO;
  }
}

function writeStoredThemeMode(theme: ThemeMode): void {
  try {
    window.localStorage.setItem(themeStorageKey, theme);
  } catch {
    return;
  }
}

function normalizeThemeMode(value: string | null): ThemeMode {
  const normalized = value?.toLowerCase();
  return normalized != null && validThemeModes.has(normalized)
    ? normalized as ThemeMode
    : ThemeMode.AUTO;
}

function applyThemeMode(theme: ThemeMode): void {
  document.documentElement.dataset.theme = theme;
}
